<template>
  <div class="page-container">
    <div class="wz-page-header">
      <h1 class="wz-page-title">{{ editing ? 'Editar descanso médico' : 'Nuevo descanso médico' }}</h1>
    </div>
    <div>
      <wz-card>
        <template #header>
          <div class="wz-card-title">Datos del descanso médico</div>
        </template>

          <!-- ============================================================ -->
          <!-- SECCIÓN 1: Datos del descanso (siempre visible)              -->
          <!-- ============================================================ -->

          <!-- Empresa y empleado -->
          <div class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/2 mb-3 px-2">
              <wz-select
                label="Empresa"
                required
                :items="companies"
                :value="companyId"
                :disabled="editing"
                defaultOption="Seleccione empresa"
                :error="$v.companyId.$error ? 'La empresa es obligatoria' : ''"
                @change="onCompanyChange"
              />
            </div>
            <div class="w-full md:w-1/2 mb-3 px-2">
              <wz-select-search
                label="Colaborador"
                required
                :items="employees"
                :value="form.employeeId"
                :disabled="editing || !companyId"
                defaultOption="Buscar por nombre o número de documento"
                :error="$v.form.employeeId.$error ? 'El colaborador es obligatorio' : ''"
                @change="onEmployeeChange"
              />
            </div>
          </div>

          <wz-alert v-if="noActiveContract" variant="danger" icon="ri-error-warning-line">
            Este colaborador no tiene contrato vigente.
          </wz-alert>

          <wz-alert v-if="complianceError" variant="danger" icon="ri-lock-line">
            <strong>Documentación incompleta.</strong> No se puede registrar el descanso médico.
            Documentos faltantes: {{ complianceError.join(', ') }}
          </wz-alert>

          <!-- Resumen del empleado -->
          <div v-if="contractInfo && !noActiveContract" class="mb-4">
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/3 px-2">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted text-sm">Empresa</div>
                  <div class="stat-value font-bold">{{ contractInfo.companyName }}</div>
                </div>
              </div>
              <div class="w-full md:w-1/3 px-2">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted text-sm">Área</div>
                  <div class="stat-value font-bold">{{ contractInfo.areaName }}</div>
                </div>
              </div>
              <div class="w-full md:w-1/3 px-2">
                <div class="stat-card p-3 border rounded text-center">
                  <div class="stat-label text-muted text-sm">Descansos activos</div>
                  <div class="stat-value font-bold text-primary">{{ summaryData.activeCount }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Resumen de días de DM acumulados en el año (aparece al elegir colaborador con año actual; al
               llenar fechas cambia al año del DM). Filtrado por empresa vía companyId. -->
          <wz-alert
            v-if="yearSummary && yearSummary.total > 0"
            variant="info"
            icon="ri-information-line"
            class="mb-4"
          >
            <strong>Días de descanso médico acumulados en {{ yearSummary.year }}</strong>
            <div class="flex flex-wrap -mx-2 mt-2">
              <div class="w-full md:w-1/3 px-2">
                <div class="text-muted text-sm">Total días DM en {{ yearSummary.year }}</div>
                <div class="font-bold">{{ yearSummary.total }}</div>
              </div>
              <div class="w-full md:w-1/3 px-2">
                <div class="text-muted text-sm">Días pagados por empleador</div>
                <div class="font-bold">{{ yearSummary.employer }}</div>
              </div>
              <div class="w-full md:w-1/3 px-2">
                <div class="text-muted text-sm">Días subsidiados por EsSalud</div>
                <div class="font-bold">{{ yearSummary.essalud }}</div>
              </div>
            </div>
          </wz-alert>

          <!-- Tipo de descanso -->
          <div v-if="form.employeeId && !noActiveContract" class="mb-3">
            <wz-select
              label="Tipo de descanso"
              required
              :items="typeOptions"
              v-model="form.leaveType"
              :disabled="editing"
              defaultOption="Seleccione tipo"
              :error="$v.form.leaveType.$error ? 'El tipo de descanso es obligatorio' : ''"
              @input="onTypeChange"
            />
          </div>

          <!-- Fechas -->
          <div v-if="form.leaveType" class="flex flex-wrap -mx-2">
            <div class="w-full md:w-1/3 px-2">
              <div class="mb-3">
                <wz-datepicker
                  label="Fecha de inicio"
                  required
                  v-model="form.startDate"
                  :disabled="editing"
                  :error="$v.form.startDate.$error ? 'La fecha de inicio es obligatoria' : ''"
                  @input="onStartDateChange"
                />
              </div>
            </div>
            <div class="w-full md:w-1/3 px-2">
              <div class="mb-3">
                <wz-datepicker
                  label="Fecha de fin"
                  required
                  v-model="form.endDate"
                  :disabled="editing || isAutoEndDate"
                  :error="$v.form.endDate.$error ? 'La fecha de fin es obligatoria' : ''"
                  @input="calculateDays"
                />
              </div>
            </div>
            <div class="w-full md:w-1/3 flex items-center px-2">
              <div v-if="totalDays !== null" class="mt-3">
                <wz-badge variant="success-solid">{{ totalDays }} días calendario</wz-badge>
                <div v-if="showDaySplit" class="text-muted text-sm mt-1">
                  {{ employerDaysNewLeave }} días empleador / {{ essaludDaysNewLeave }} días EsSalud
                </div>
              </div>
            </div>
          </div>

          <!-- Alerta UX: superposición con un DM activo (el bloqueo real lo hace el backend) -->
          <wz-alert v-if="overlapsWithActiveDM" variant="warning" icon="ri-error-warning-line" class="mt-1 mb-2">
            Las fechas coinciden con un descanso médico activo. Al guardar, el sistema rechazará el registro.
          </wz-alert>

          <!-- Advertencia UX: solape con vacaciones APROBADAS. NO bloquea (el DM tiene precedencia legal). -->
          <wz-alert v-if="vacationOverlap && vacationOverlap.overlaps" variant="warning" icon="ri-error-warning-line" class="mt-1 mb-2">
            El colaborador tiene vacaciones registradas del {{ vacationOverlap.vacationStart.split('-').reverse().join('/') }}
            al {{ vacationOverlap.vacationEnd.split('-').reverse().join('/') }} que coinciden con las fechas de este descanso médico.
            Verifique si corresponde ajustar las vacaciones.
          </wz-alert>

          <!-- Indicador: el nuevo DM supera el tope anual de 20 días del empleador -->
          <wz-alert v-if="limiteAlcanzado" variant="warning" icon="ri-error-warning-line" class="mt-1 mb-2">
            Con este descanso médico se superan los 20 días pagados por el empleador.
            Los días adicionales serán subsidiados por EsSalud.
          </wz-alert>

          <!-- Notas informativas por tipo -->
          <wz-alert v-if="form.leaveType === 'MATERNIDAD' && form.startDate" variant="info" icon="ri-information-line">
            Maternidad: 49 días prenatal + 49 días postnatal = 98 días calendario
          </wz-alert>
          <wz-alert v-if="form.leaveType === 'PATERNIDAD' && form.startDate" variant="info" icon="ri-information-line">
            Paternidad: 10 días calendario según Ley 29409
          </wz-alert>

          <!-- ============================================================ -->
          <!-- SECCIÓN 2: Datos médicos (v-if tipo != PATERNIDAD)           -->
          <!-- ============================================================ -->
          <template v-if="showMedicalSection">
            <hr class="my-4">
            <h5 class="mb-3 font-bold">Datos médicos</h5>

            <!-- Tipo de atención (solo enfermedad/accidentes) -->
            <div v-if="showCareTypeSelector" class="mb-3">
              <wz-select
                label="Tipo de atención"
                :items="careTypeOptions"
                v-model="form.careType"
                defaultOption="Seleccione tipo de atención"
                @input="onCareTypeChange"
              />
            </div>

            <!-- Pago por día (base del subsidio). Siempre editable: el caso puede pasar a SUBSIDIO por
                 acumulación anual aunque este DM tenga ≤20 días. Sugerido = salario base / 30 (del backend). -->
            <div class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Pago por día (S/)"
                  type="number"
                  step="0.01"
                  min="0"
                  v-model.number="form.dailyPayment"
                  placeholder="Monto diario del subsidio"
                />
                <small class="text-muted block" v-if="suggestedDailyPayment != null">
                  Sugerido: S/ {{ Number(suggestedDailyPayment).toFixed(2) }} (salario base / 30).
                </small>
                <small class="text-warning block">
                  Verificar contra el promedio real de las últimas 4 remuneraciones para colaboradores con aumento reciente o variables.
                </small>
              </div>
            </div>

            <!-- EsSalud: N° CITT -->
            <div v-if="showEssaludFields" class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Número CITT"
                  v-model="form.cittNumber"
                  placeholder="Número de CITT"
                />
              </div>
            </div>

            <!-- Particular: doctor + especialidad + colegio médico + centro médico -->
            <div v-if="showParticularFields" class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Centro médico"
                  v-model="form.medicalCenter"
                  placeholder="Nombre del centro médico"
                />
              </div>
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Especialidad"
                  required
                  v-model="form.doctorSpecialty"
                  placeholder="Ej: Medicina Intensiva, Traumatología"
                  :error="$v.form.doctorSpecialty.$error ? 'La especialidad es obligatoria' : ''"
                />
              </div>
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Nombre del doctor"
                  required
                  v-model="form.doctorName"
                  placeholder="Ej: LANCHIPA PICO HOGAR"
                  :error="$v.form.doctorName.$error ? 'El nombre del doctor es obligatorio' : ''"
                />
              </div>
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="N° Colegio Médico"
                  :value="form.medicalCollegeNumber"
                  :maxlength="6"
                  placeholder="N° Colegio Médico (CMP)"
                  :error="$v.form.medicalCollegeNumber.$error ? 'Solo números, máximo 6 dígitos' : ''"
                  @input="onCollegeNumberInput"
                />
              </div>
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-datepicker
                  label="Fecha de canje CITT"
                  v-model="form.cittExchangeDate"
                />
              </div>
              <div class="w-full px-2 mb-3">
                <wz-textarea
                  label="Diagnóstico"
                  required
                  v-model="form.diagnosis"
                  :rows="3"
                  placeholder="Diagnóstico médico"
                  :error="$v.form.diagnosis.$error ? 'El diagnóstico es obligatorio' : ''"
                />
              </div>
              <div class="w-full px-2 mb-3">
                <label class="wz-form-label">
                  Certificado médico
                  <span class="text-muted text-sm ml-1">(PDF/JPG/PNG, opcional)</span>
                </label>
                <wz-file-input
                  class="w-full mt-2"
                  id-file="medical-leave-certificate"
                  :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
                  @onChange="onCertificateChange"
                />
              </div>
            </div>

            <!-- Maternidad: doctor + especialidad (opcional) -->
            <div v-if="showMaternityDoctor" class="flex flex-wrap -mx-2">
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Nombre del doctor"
                  v-model="form.doctorName"
                  placeholder="Ej: LANCHIPA PICO HOGAR"
                />
              </div>
              <div class="w-full md:w-1/2 px-2 mb-3">
                <wz-input
                  label="Especialidad"
                  v-model="form.doctorSpecialty"
                  placeholder="Ej: Ginecología"
                />
              </div>
            </div>

            <!-- Institución médica -->
            <div class="mb-3">
              <wz-input
                label="Institución médica"
                v-model="form.medicalInstitution"
                placeholder="Nombre de la institución médica"
              />
            </div>

            <!-- Diagnóstico se captura solo en atención Particular (ver bloque arriba) -->
          </template>

          <!-- ============================================================ -->
          <!-- SECCIÓN 3: Subsidio y trámite                                -->
          <!-- (v-if tipo != PATERNIDAD Y (totalDays > 20 O MATERNIDAD))    -->
          <!-- ============================================================ -->
          <template v-if="showSubsidySection">
            <hr class="my-4">
            <h5 class="mb-3 font-bold">Subsidio y trámite EsSalud</h5>

            <div class="flex flex-wrap -mx-2">
              <!-- Estado del caso -->
              <div class="w-full md:w-1/3 px-2">
                <div class="mb-3">
                  <wz-select
                    label="Estado del caso"
                    :items="caseStatusOptions"
                    v-model="form.caseStatus"
                    :disabled="form.leaveType === 'MATERNIDAD'"
                  />
                </div>
              </div>

              <!-- Tipo de seguro -->
              <div class="w-full md:w-1/3 px-2">
                <div class="mb-3">
                  <wz-select
                    label="Tipo de seguro"
                    :items="insuranceTypeOptions"
                    v-model="form.insuranceType"
                    defaultOption="Seleccione"
                    :disabled="form.leaveType === 'MATERNIDAD'"
                  />
                </div>
              </div>

              <!-- Estado del trámite (v-if caseStatus = SUBSIDIO) -->
              <div class="w-full md:w-1/3 px-2" v-if="showProcessFields">
                <div class="mb-3">
                  <wz-select
                    label="Estado del trámite"
                    :items="processStatusOptions"
                    v-model="form.processStatus"
                    defaultOption="Seleccione"
                  />
                </div>
              </div>
            </div>

            <!-- Montos (v-if caseStatus = SUBSIDIO). El pago por día se ingresa arriba (siempre visible). -->
            <div class="flex flex-wrap -mx-2" v-if="showProcessFields">
              <div class="w-full md:w-1/3 px-2">
                <div class="mb-3">
                  <wz-input
                    label="Monto pagado (S/)"
                    :value="calculatedAmountPaid !== null ? calculatedAmountPaid.toFixed(2) : '-'"
                    readonly
                  />
                  <small class="text-muted" v-if="subsidizedDays > 0">
                    {{ form.dailyPayment || 0 }} x {{ subsidizedDays }} días = S/ {{ calculatedAmountPaid !== null ? calculatedAmountPaid.toFixed(2) : '0.00' }}
                  </small>
                </div>
              </div>
              <div class="w-full md:w-1/3 px-2" v-if="form.processStatus === 'COBRADO'">
                <div class="mb-3">
                  <wz-input
                    label="Monto cobrado (S/)"
                    type="number"
                    step="0.01"
                    min="0"
                    v-model.number="form.amountCollected"
                    placeholder="Monto reembolsado por EsSalud"
                  />
                </div>
              </div>
            </div>
          </template>

          <!-- ============================================================ -->
          <!-- SECCIÓN 4: Gestión (siempre visible cuando hay tipo)         -->
          <!-- ============================================================ -->
          <template v-if="form.leaveType">
            <hr class="my-4">
            <h5 class="mb-3 font-bold">Gestión</h5>

            <!-- Encargado -->
            <div class="mb-3">
              <wz-input
                label="Encargado"
                v-model="form.personInCharge"
                placeholder="Persona responsable del trámite"
              />
            </div>

            <!-- Archivo adjunto -->
            <div class="mb-3">
              <label class="wz-form-label">Archivo adjunto <small class="text-muted">(PDF, JPG, JPEG, PNG — máx. 10MB)</small></label>
              <wz-file-input
                :key="fileKey"
                class="w-full mt-2"
                id-file="medical-leave-attachment"
                :accept-file="['.pdf', '.jpg', '.jpeg', '.png']"
                @onChange="onFileChange"
              />
            </div>

            <!-- Observaciones -->
            <div class="mb-3">
              <wz-textarea
                label="Observaciones"
                v-model="form.observations"
                rows="3"
                placeholder="Observaciones adicionales"
              />
            </div>
          </template>

          <!-- Botones -->
          <div class="mb-3 text-right text-right mt-4">
            <wz-button variant="light" class="mr-2" @click="onCancel">
              Cancelar
            </wz-button>
            <wz-button
              variant="primary"
              @click="save()"
            >
              Guardar
            </wz-button>
          </div>

      </wz-card>
    </div>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { required, requiredIf, numeric, maxLength } from 'vuelidate/lib/validators'
import { MedicalLeaveService, EmployeeService, VacationService, CompanyService } from '@/core/services'
import { Constants } from '@/core/utils'
import { isFullSubsidyNoEmployer } from '@/core/utils/subsidyRegime'

const MATERNIDAD_DAYS = 98
const PATERNIDAD_DAYS = 10

export default {
  name: 'MedicalLeaveCreate',
  
  data() {
    return {
      employees: [],
      companies: [],
      companyId: null,
      contractInfo: null,
      summaryData: { activeCount: 0 },
      previewData: null,   // split year-aware del DM prospectivo (backend) — alimenta el split y el panel
      previewTimer: null,  // debounce del preview
      vacationOverlap: null,     // { overlaps, vacationStart, vacationEnd } del chequeo de solape con vacaciones
      vacationOverlapTimer: null, // debounce del chequeo (300ms)
      noActiveContract: false,
      complianceError: null,
      selectedFile: null,
      fileKey: 0,
      editing: false,
      isLoadingEdit: false,
      selectedCertificate: null,
      form: {
        employeeId:         null,
        leaveType:          null,
        startDate:          null,
        endDate:            null,
        cittNumber:         '',
        cittExchangeDate:   null,
        medicalCollegeNumber: '',
        medicalCenter:      '',
        careType:           null,
        medicalInstitution: '',
        diagnosis:          '',
        observations:       '',
        doctorName:         null,
        doctorSpecialty:    null,
        caseStatus:         'DESCANSO_MEDICO',
        processStatus:      null,
        insuranceType:      null,
        dailyPayment:       null,
        amountCollected:    null,
        personInCharge:     null,
      },
      typeOptions: Object.values(Constants.MedicalLeaveType).map(t => ({ key: t.key, label: t.label })),
      careTypeOptions: Object.values(Constants.MedicalLeaveCareType).map(t => ({ key: t.key, label: t.label })),
      caseStatusOptions: Object.values(Constants.MedicalLeaveCaseStatus).map(t => ({ key: t.key, label: t.label })),
      processStatusOptions: Object.values(Constants.MedicalLeaveProcessStatus).map(t => ({ key: t.key, label: t.label })),
      insuranceTypeOptions: Object.values(Constants.MedicalLeaveInsuranceType).map(t => ({ key: t.key, label: t.label })),
    }
  },
  validations() {
    return {
      companyId: { required },
      form: {
        employeeId: { required },
        leaveType:  { required },
        startDate:  { required },
        endDate:    { required },
        doctorName: {
          required: requiredIf(() => this.showParticularFields),
        },
        doctorSpecialty: {
          required: requiredIf(() => this.showParticularFields),
        },
        medicalCollegeNumber: {
          numeric,
          maxLength: maxLength(6),
        },
        diagnosis: {
          required: requiredIf(() => this.showParticularFields),
        },
      },
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    },
    // Año de inicio del DM (el contador de 20 días se ancla a este año, no al calendario actual).
    leaveYear() {
      return this.form.startDate ? Number(this.form.startDate.slice(0, 4)) : this.currentYear
    },
    // Datos del panel morado. Con fechas: acumulado del año de inicio del DM (preview). Sin fechas (recién
    // elegido el colaborador): acumulado del AÑO ACTUAL. Ambos ya vienen filtrados por empresa (companyId).
    yearSummary() {
      if (this.previewData) {
        return {
          year: this.leaveYear,
          total: this.previewData.accumulatedDaysYear,
          employer: this.previewData.employerDaysYear,
          essalud: this.previewData.essaludDaysYear,
        }
      }
      if (this.form.employeeId && this.summaryData && this.summaryData.totalDaysThisYear != null) {
        return {
          year: this.currentYear,
          total: this.summaryData.totalDaysThisYear,
          employer: this.summaryData.employerDaysThisYear,
          essalud: this.summaryData.essaludDaysThisYear,
        }
      }
      return null
    },
    totalDays() {
      if (!this.form.startDate || !this.form.endDate) return null
      const start = new Date(this.form.startDate + 'T00:00:00')
      const end   = new Date(this.form.endDate   + 'T00:00:00')
      const diff  = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
      return diff > 0 ? diff : null
    },
    // Alerta UX: ¿las fechas del nuevo DM se superponen con algún DM ACTIVO del colaborador?
    // Solo cubre activeLeaves (no finalizados). El bloqueo real lo hace el backend al guardar.
    overlapsWithActiveDM() {
      if (!this.form.startDate || !this.form.endDate) return false
      const active = this.summaryData && this.summaryData.activeLeaves
      if (!active || !active.length) return false
      const editingId = this.editing ? String(this.$route.params.id) : null
      return active.some(l => {
        if (editingId && String(l.id) === editingId) return false // excluir el propio DM en edición
        if (!l.startDate || !l.endDate) return false
        // Fechas YYYY-MM-DD: comparables como strings directamente.
        return this.form.startDate <= l.endDate && this.form.endDate >= l.startDate
      })
    },
    // Split del NUEVO DM. Para enfermedad/accidentes viene del preview year-aware del backend (misma
    // lógica que la persistencia, incluida la Regla B al cruzar años). MATERNIDAD/PATERNIDAD: régimen fijo.
    employerDaysNewLeave() {
      if (this.totalDays === null) return 0
      if (this.form.leaveType === 'MATERNIDAD') return 0
      if (this.form.leaveType === 'PATERNIDAD') return this.totalDays
      return this.previewData ? this.previewData.employerDays : 0
    },
    essaludDaysNewLeave() {
      if (this.totalDays === null) return 0
      if (this.form.leaveType === 'MATERNIDAD') return this.totalDays
      if (this.form.leaveType === 'PATERNIDAD') return 0
      return this.previewData ? this.previewData.essaludDays : 0
    },
    // subsidizedDays se mantiene: lo usa la sección "Subsidio y trámite EsSalud" (fuera de alcance).
    subsidizedDays() {
      if (this.totalDays === null) return 0
      if (this.form.leaveType === 'MATERNIDAD') return this.totalDays
      if (this.form.leaveType === 'PATERNIDAD') return 0
      return Math.max(0, this.totalDays - 20)
    },
    calculatedAmountPaid() {
      if (!this.form.dailyPayment || !this.subsidizedDays) return null
      return this.form.dailyPayment * this.subsidizedDays
    },
    showMedicalSection() {
      return this.form.leaveType && this.form.leaveType !== 'PATERNIDAD'
    },
    showSubsidySection() {
      // Sin gate de >20 días: el caso puede pasar a SUBSIDIO por acumulación anual aunque este DM tenga
      // ≤20 días propios. RRHH necesita poder marcar el caso e ingresar montos (pago diario / cobrado).
      return this.form.leaveType && this.form.leaveType !== 'PATERNIDAD'
    },
    showCareTypeSelector() {
      return ['ENFERMEDAD', 'ACCIDENTE_COMUN', 'ACCIDENTE_LABORAL'].includes(this.form.leaveType)
    },
    showEssaludFields() {
      return this.showCareTypeSelector && this.form.careType === 'ESSALUD'
    },
    showParticularFields() {
      return this.showCareTypeSelector && this.form.careType === 'PARTICULAR'
    },
    showMaternityDoctor() {
      return this.form.leaveType === 'MATERNIDAD'
    },
    showProcessFields() {
      return this.form.caseStatus === 'SUBSIDIO'
    },
    // Pago diario sugerido (salario base / 30) que expone el summary del backend, para precargar el input.
    suggestedDailyPayment() {
      return this.summaryData && this.summaryData.suggestedDailyPayment != null
        ? this.summaryData.suggestedDailyPayment
        : null
    },
    showDaySplit() {
      if (this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD') return true
      return !!(this.totalDays && this.previewData)
    },
    // El nuevo DM hace que se superen los 20 días anuales pagados por el empleador → hay días EsSalud.
    // Régimen aparte (MATERNIDAD, o ACCIDENTE_LABORAL con SCTR) NO consume el contador de 20 días → sin alerta.
    // PATERNIDAD (migrada a Licencias) tampoco aplica. Decisión centralizada en el helper.
    limiteAlcanzado() {
      if (!this.totalDays) return false
      if (this.form.leaveType === 'PATERNIDAD') return false
      const hasSctr = !!(this.summaryData && this.summaryData.hasSctrHealth === true)
      if (isFullSubsidyNoEmployer(this.form.leaveType, hasSctr)) return false
      return this.previewData ? this.previewData.essaludDays > 0 : false
    },
    isAutoEndDate() {
      return this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD'
    },
  },
  watch: {
    'form.leaveType'(newType) {
      if (this.isLoadingEdit) return
      if (newType === 'PATERNIDAD') {
        this.form.doctorName = null
        this.form.doctorSpecialty = null
        this.form.caseStatus = 'DESCANSO_MEDICO'
        this.form.processStatus = null
        this.form.insuranceType = null
        this.form.dailyPayment = null
        this.form.amountCollected = null
        this.form.cittNumber = ''
        this.form.diagnosis = ''
        this.clearCareFields()
      } else if (newType === 'MATERNIDAD') {
        this.form.caseStatus = 'SUBSIDIO'
        this.form.insuranceType = 'ESSALUD'
        this.form.cittNumber = ''
        this.clearCareFields()
      } else {
        // ENFERMEDAD / ACCIDENTE_COMUN / ACCIDENTE_LABORAL
        if (this.totalDays && this.totalDays > 20) {
          this.form.caseStatus = 'SUBSIDIO'
        } else {
          this.form.caseStatus = 'DESCANSO_MEDICO'
        }
      }
      this.schedulePreview()
      this.scheduleVacationCheck()
    },
    totalDays(newDays) {
      if (this.isLoadingEdit) return
      this.schedulePreview()
      this.scheduleVacationCheck()
      if (this.form.leaveType === 'MATERNIDAD' || this.form.leaveType === 'PATERNIDAD') return
      if (newDays && newDays > 20) {
        this.form.caseStatus = 'SUBSIDIO'
      } else {
        this.form.caseStatus = 'DESCANSO_MEDICO'
      }
    },
    'form.caseStatus'(newStatus) {
      if (this.isLoadingEdit) return
      if (newStatus !== 'SUBSIDIO') {
        this.form.processStatus = null
        this.form.amountCollected = null
        // dailyPayment NO se borra: se ingresa siempre (el caso puede pasar a SUBSIDIO por acumulación anual).
      } else if (!this.form.processStatus) {
        this.form.processStatus = 'PENDIENTE'
      }
    },
  },
  methods: {
    // N° Colegio Médico: solo dígitos, máximo 6
    onCollegeNumberInput(val) {
      this.form.medicalCollegeNumber = (val || '').replace(/\D/g, '').slice(0, 6)
    },
    onCertificateChange(files) {
      this.selectedCertificate = files && files.length ? files[0] : null
    },
    // Cancelar: si se llegó desde la ficha del colaborador (?employeeId), regresar a ella
    // en el tab de Descansos médicos; si no, a la lista general de DMs.
    onCancel() {
      const employeeId = this.$route.query.employeeId
      if (employeeId) {
        this.$router.push({ name: 'employee-details', params: { id: String(employeeId) }, query: { tab: 'medical' } })
      } else {
        this.$router.push({ name: 'medical-leave-list' })
      }
    },
    // Limpia los campos asociados al tipo de atención (CITT/canje, doctor/colegio/centro, diagnóstico, certificado)
    clearCareFields() {
      this.form.careType = null
      this.form.cittNumber = ''
      this.form.cittExchangeDate = null
      this.form.medicalCollegeNumber = ''
      this.form.medicalCenter = ''
      this.form.diagnosis = ''
      this.selectedCertificate = null
    },
    // Al cambiar el tipo de atención, limpiar los campos del tipo no seleccionado
    onCareTypeChange(value) {
      if (value === 'ESSALUD') {
        this.form.doctorName = null
        this.form.doctorSpecialty = null
        this.form.medicalCollegeNumber = ''
        this.form.medicalCenter = ''
        this.form.diagnosis = ''
        this.form.cittExchangeDate = null
        this.selectedCertificate = null
      } else if (value === 'PARTICULAR') {
        this.form.cittNumber = ''
      }
    },
    async onCompanyChange(companyId) {
      this.companyId = companyId || null
      this.onEmployeeChange(null)
      await this.loadEmployees()
    },
    // Preview year-aware del split (debounced). Fuente única: mismo cálculo que la persistencia (backend).
    schedulePreview() {
      if (this.previewTimer) clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(() => this.fetchPreview(), 300)
    },
    async fetchPreview() {
      // Solo enfermedad/accidentes usan el preview year-aware; MATERNIDAD/PATERNIDAD tienen régimen fijo.
      const isIllness = ['ENFERMEDAD', 'ACCIDENTE_COMUN', 'ACCIDENTE_LABORAL'].includes(this.form.leaveType)
      if (!isIllness || !this.form.employeeId || !this.companyId || !this.form.startDate || !this.form.endDate) {
        this.previewData = null
        return
      }
      try {
        const resp = await MedicalLeaveService.previewDays(
          this.form.employeeId, this.companyId, this.form.startDate, this.form.endDate)
        this.previewData = resp.payload || null
      } catch (e) {
        this.previewData = null
      }
    },
    // Chequeo (debounced 300ms) de solape con vacaciones APROBADAS. Solo advertencia visual: NO bloquea el envío.
    scheduleVacationCheck() {
      if (this.vacationOverlapTimer) clearTimeout(this.vacationOverlapTimer)
      this.vacationOverlapTimer = setTimeout(() => this.runVacationCheck(), 300)
    },
    async runVacationCheck() {
      if (!this.form.employeeId || !this.form.startDate || !this.form.endDate) {
        this.vacationOverlap = null
        return
      }
      try {
        const resp = await MedicalLeaveService.checkVacationOverlap(
          this.form.employeeId, this.form.startDate, this.form.endDate)
        this.vacationOverlap = (resp.payload && resp.payload.overlaps) ? resp.payload : null
      } catch (e) {
        this.vacationOverlap = null // el chequeo es solo advertencia; un error no debe romper el flujo
      }
    },
    async loadEmployees() {
      try {
        const resp = this.companyId
          ? await EmployeeService.listByCompany(this.companyId)
          : await EmployeeService.listActive()
        const seen = new Set()
        this.employees = (resp.payload || [])
          .filter(e => e && e.id != null && !seen.has(e.id) && seen.add(e.id))
          .map(e => ({ key: e.id, label: e.description ?? '' }))
      } catch (e) {
        console.error(e)
        this.employees = []
      }
    },
    async onEmployeeChange(employeeId) {
      this.form.employeeId = employeeId
      this.contractInfo    = null
      this.summaryData     = { activeCount: 0 }
      this.previewData     = null
      this.noActiveContract = false
      this.complianceError  = null
      this.form.leaveType  = null
      this.form.startDate  = null
      this.form.endDate    = null
      if (!employeeId) return
      try {
        this.$store.dispatch('app/loading', true)
        const balanceResp = await VacationService.getBalanceByEmployeeCompany(employeeId, this.companyId)
        const balance = balanceResp.payload
        if (!balance || !balance.contractId) {
          this.noActiveContract = true
          return
        }
        this.contractInfo = {
          companyName: balance.companyName || '-',
          areaName:    balance.laborRegime || '-',
        }
        try {
          // Acumulado del año por EMPRESA seleccionada (el contador de 20 días es por empresa).
          const summaryResp = await MedicalLeaveService.getSummary(employeeId, this.companyId)
          this.summaryData = summaryResp.payload || { activeCount: 0 }
          // Precargar el pago diario sugerido (salario / 30) solo en alta y si el usuario aún no ingresó uno.
          if (!this.isLoadingEdit && (this.form.dailyPayment == null || this.form.dailyPayment === '')
              && this.summaryData.suggestedDailyPayment != null) {
            this.form.dailyPayment = this.summaryData.suggestedDailyPayment
          }
        } catch (e) {
          // non-critical
        }
        try {
          const compResp = await EmployeeService.getCompliance(employeeId)
          const p = compResp.payload
          this.complianceError = p.compliant ? null : p.missingDocuments
        } catch (e) { /* non-critical */ }
      } catch (e) {
        this.noActiveContract = true
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    onTypeChange() {
      this.form.startDate = null
      this.form.endDate   = null
    },
    onStartDateChange() {
      if (!this.form.startDate) {
        this.form.endDate = null
        return
      }
      if (this.form.leaveType === 'MATERNIDAD') {
        this.autoFillEndDate(MATERNIDAD_DAYS)
      } else if (this.form.leaveType === 'PATERNIDAD') {
        this.autoFillEndDate(PATERNIDAD_DAYS)
      }
    },
    autoFillEndDate(days) {
      const start = new Date(this.form.startDate + 'T00:00:00')
      start.setDate(start.getDate() + days - 1)
      this.form.endDate = start.toISOString().split('T')[0]
    },
    calculateDays() {
      // totalDays is a computed property, no manual action needed
    },
    onFileChange(files) {
      this.selectedFile = files && files.length ? files[0] : null
    },
    async loadForEdit(id) {
      try {
        this.$store.dispatch('app/loading', true)
        this.isLoadingEdit = true
        const payload = (await MedicalLeaveService.get(id)).payload
        // Bloqueados (solo para mostrar): empresa y colaborador como ítem único
        this.companyId = payload.companyName ? 'current' : null
        this.companies = payload.companyName ? [{ key: 'current', label: payload.companyName }] : []
        this.employees = payload.employeeId ? [{ key: payload.employeeId, label: payload.employeeName }] : []
        // Campos del form
        this.form.employeeId         = payload.employeeId
        this.form.leaveType          = payload.leaveType
        this.form.startDate          = payload.startDate
        this.form.endDate            = payload.endDate
        this.form.careType           = payload.careType
        this.form.cittNumber         = payload.cittNumber || ''
        this.form.cittExchangeDate   = payload.cittExchangeDate
        this.form.medicalCollegeNumber = payload.medicalCollegeNumber || ''
        this.form.medicalCenter      = payload.medicalCenter || ''
        this.form.medicalInstitution = payload.medicalInstitution || ''
        this.form.diagnosis          = payload.diagnosis || ''
        this.form.observations       = payload.observations || ''
        this.form.doctorName         = payload.doctorName
        this.form.doctorSpecialty    = payload.doctorSpecialty
        this.form.caseStatus         = payload.caseStatus || 'DESCANSO_MEDICO'
        this.form.processStatus      = payload.processStatus
        this.form.insuranceType      = payload.insuranceType
        this.form.dailyPayment       = payload.dailyPayment
        this.form.amountCollected    = payload.amountCollected
        this.form.personInCharge     = payload.personInCharge
        // Liberar el guard luego del flush de watchers (totalDays/leaveType/caseStatus)
        this.$nextTick(() => { this.isLoadingEdit = false })
      } catch (e) {
        console.error(e)
        Alert.error('Error al cargar el descanso médico')
        this.$router.push({ name: 'medical-leave-list' })
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async save() {
      this.$v.$touch()
      if (this.$v.$error) {
        this.$nextTick(() => {
          const errorEl = document.querySelector('.wz-form-error-msg, .form-error')
          if (errorEl) this.$smoothScroll({ scrollTo: errorEl, updateHistory: false, offset: -50 })
        })
        return
      }
      if (this.noActiveContract || this.complianceError) return
      try {
        this.$store.dispatch('app/loading', true)
        const fd = new FormData()
        fd.append('employeeId', this.form.employeeId)
        fd.append('companyId',  this.companyId)
        fd.append('leaveType',  this.form.leaveType)
        fd.append('startDate',  this.form.startDate)
        fd.append('endDate',    this.form.endDate)
        if (this.form.careType)
          fd.append('careType', this.form.careType)
        if (this.form.cittNumber && this.form.cittNumber.trim())
          fd.append('cittNumber', this.form.cittNumber.trim())
        if (this.form.cittExchangeDate)
          fd.append('cittExchangeDate', this.form.cittExchangeDate)
        if (this.form.medicalCollegeNumber && this.form.medicalCollegeNumber.trim())
          fd.append('medicalCollegeNumber', this.form.medicalCollegeNumber.trim())
        if (this.form.medicalCenter && this.form.medicalCenter.trim())
          fd.append('medicalCenter', this.form.medicalCenter.trim())
        if (this.form.medicalInstitution && this.form.medicalInstitution.trim())
          fd.append('medicalInstitution', this.form.medicalInstitution.trim())
        if (this.form.diagnosis && this.form.diagnosis.trim())
          fd.append('diagnosis', this.form.diagnosis.trim())
        if (this.form.observations && this.form.observations.trim())
          fd.append('observations', this.form.observations.trim())
        if (this.form.doctorName && this.form.doctorName.trim())
          fd.append('doctorName', this.form.doctorName.trim())
        if (this.form.doctorSpecialty && this.form.doctorSpecialty.trim())
          fd.append('doctorSpecialty', this.form.doctorSpecialty.trim())
        fd.append('caseStatus', this.form.caseStatus)
        if (this.form.processStatus)
          fd.append('processStatus', this.form.processStatus)
        if (this.form.insuranceType)
          fd.append('insuranceType', this.form.insuranceType)
        if (this.form.dailyPayment != null)
          fd.append('dailyPayment', this.form.dailyPayment)
        if (this.form.amountCollected != null)
          fd.append('amountCollected', this.form.amountCollected)
        if (this.form.personInCharge && this.form.personInCharge.trim())
          fd.append('personInCharge', this.form.personInCharge.trim())
        if (this.selectedFile)
          fd.append('file', this.selectedFile)
        if (this.selectedCertificate)
          fd.append('certificateFile', this.selectedCertificate)
        if (this.editing) {
          await MedicalLeaveService.update(this.$route.params.id, fd)
          Alert.success('Descanso médico actualizado correctamente')
        } else {
          await MedicalLeaveService.create(fd)
          Alert.success('Descanso médico registrado correctamente')
        }
        this.$router.push({ name: 'medical-leave-list' })
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : `Error al ${this.editing ? 'actualizar' : 'registrar'} el descanso médico`)
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
  },
  async created() {
    if (this.$route.name === 'medical-leave-edit' && this.$route.params.id) {
      this.editing = true
      await this.loadForEdit(this.$route.params.id)
      return
    }
    try {
      const companiesResp = await CompanyService.listAll()
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.name }))
    } catch (e) {
      console.error(e)
    }
    // Prefill desde la ficha del colaborador (?companyId=&employeeId=). Empresa primero: al fijarla se
    // cargan sus colaboradores; luego se preselecciona el colaborador. Si no viene companyId, el usuario
    // elige empresa (obligatoria) y recién ahí el colaborador.
    if (this.$route.query.companyId) {
      await this.onCompanyChange(Number(this.$route.query.companyId))
      if (this.$route.query.employeeId) {
        this.onEmployeeChange(Number(this.$route.query.employeeId))
      }
    }
  },
  beforeDestroy() {
    if (this.previewTimer) clearTimeout(this.previewTimer)
  },
}
</script>
